import { useTheme } from '../../hooks/useTheme';
import { BILLING_TERMS, type BillingTerm } from '../../hooks/useCurrency';

interface BillingTermToggleProps {
  term: BillingTerm;
  onChange: (next: BillingTerm) => void;
  /** Texto de cada pestaña (Mensual · Semestral · Anual). */
  labels: Record<BillingTerm, string>;
  /** Distintivo opcional junto a la etiqueta (−10%, 2 meses gratis…). */
  badges?: Partial<Record<BillingTerm, string>>;
  /** Fondo de la pestaña activa. Por defecto, el azul eléctrico de la marca. */
  activeBackground?: string;
  activeColor?: string;
  ariaLabel?: string;
}

/**
 * Selector de término de facturación compartido por los tres bloques de planes
 * (generales, dealers y odontología). El descuento de cada término es
 * permanente: se calcula en `useCurrency`, no aquí.
 */
export default function BillingTermToggle({
  term,
  onChange,
  labels,
  badges,
  activeBackground = 'linear-gradient(135deg, #00cfff, #0284c7)',
  activeColor = '#0a1628',
  ariaLabel = 'Seleccionar forma de pago',
}: BillingTermToggleProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  return (
    <div
      className="inline-flex flex-col sm:flex-row items-stretch sm:items-center gap-1 rounded-2xl sm:rounded-full px-1 py-1 border w-full sm:w-auto"
      role="group"
      aria-label={ariaLabel}
      style={{
        backgroundColor: isLight ? '#e8eef8' : 'rgba(12,22,48,0.8)',
        borderColor: isLight ? '#c5d4e8' : 'rgba(255,255,255,0.10)',
      }}
    >
      {BILLING_TERMS.map((id) => {
        const active = term === id;
        const badge = badges?.[id];
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            aria-pressed={active}
            className="flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-full text-sm font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 touch-manipulation"
            style={{
              background: active ? activeBackground : 'transparent',
              color: active
                ? activeColor
                : isLight
                  ? 'rgba(10,22,40,0.55)'
                  : 'rgba(255,255,255,0.60)',
            }}
          >
            {labels[id]}
            {badge && (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                style={
                  active
                    ? { backgroundColor: 'rgba(10,22,40,0.18)', color: activeColor }
                    : { backgroundColor: 'rgba(0,207,255,0.18)', color: '#00cfff' }
                }
              >
                {badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
